import Alert from '@mui/material/Alert';
import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { CONFIRM_DELETE, FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  IStoriesFolder,
  IStoryListMessages,
  IStoryListSelection
} from '../../../../types';
import {
  areFoldersNestedTooDeep,
  countAncestors,
  disableNodeAndDescendants,
  getNodeDescedentIds,
  transformFolderStructureToMap,
  updateFolderStructureNode
} from '../../../utils';
import { generateUniqueFolderName } from '../CopyFolderModal';
import {
  useDeleteStoryFolders,
  useFoldersStoryCount,
  useMoveFolders,
  useMoveStories,
  useRenameStoryFolder
} from '../hooks';
import { useToast } from '../../Toast/context/useToast.context';

import { styles } from './DeleteFolderModal.styles';

const MOVE_STORIES = 'MOVE_STORIES';
const DELETE_STORIES = 'DELETE_STORIES';

export interface IDeleteFolderModalProps extends RouteComponentProps {
  currentFolderId: string;
  currentFolderStoryCount: number;
  folderStructure: IStoriesFolder[];
  messages: IStoryListMessages;
  entityId: number;
  entitySubdomain: string;
  onClose?(): void;
  setSelectedList(newList: IStoryListSelection, entitySubdomain: string): void;
}

export const DeleteFolderModal: React.FC<IDeleteFolderModalProps> = (props) => {
  const {
    currentFolderId,
    currentFolderStoryCount,
    folderStructure,
    messages,
    onClose,
    setSelectedList,
    entityId,
    entitySubdomain
  } = props;

  const { toggleToast } = useToast();

  const deleteStoryFoldersMutation = useDeleteStoryFolders({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderDeleteFailed
  });
  const moveFoldersMutation = useMoveFolders({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderMoveFailed
  });
  const moveStoriesMutation = useMoveStories({ title: messages.somethingWentWrongError });
  const renameStoryFolderMutation = useRenameStoryFolder({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderRenameFailed
  });

  const [postFolderDeleteAction, setPostFolderDeleteAction] = useState(MOVE_STORIES);
  const [confirmationText, setConfirmationText] = useState('');
  const [targetFolderId, setTargetFolderId] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [accordionOptions, setAccordionOptions] = useState([]);

  const folderMap = useMemo(() => transformFolderStructureToMap(folderStructure), [folderStructure]);
  const currentFolder = folderMap?.[currentFolderId];
  const currentFolderName = currentFolder.displayName;

  const descendentFolderIds = getNodeDescedentIds(currentFolder);

  const storiesCountQuery = useFoldersStoryCount(
    descendentFolderIds?.length > 0 ? { folderIds: descendentFolderIds } : null,
    messages.failedToGetStoriesInFolderCount
  );
  const storiesInSubfoldersCount = storiesCountQuery.data ?? 0;
  const totalStoryCount = currentFolderStoryCount + storiesInSubfoldersCount;

  useEffect(() => {
    if (confirmationText !== '') {
      setConfirmationText('');
    }
  }, [postFolderDeleteAction, targetFolderId]);

  useEffect(() => {
    const disabledNodeAndDescendents = disableNodeAndDescendants(currentFolder);
    setAccordionOptions(updateFolderStructureNode(folderStructure, disabledNodeAndDescendents));
  }, [folderStructure, currentFolder]);

  const subfolderCount = currentFolder?.children?.length;

  const shouldMoveStoriesOrFolders =
    (totalStoryCount > 0 || subfolderCount > 0) && postFolderDeleteAction === MOVE_STORIES;

  const targetFolderDepth = countAncestors(folderStructure, targetFolderId);

  const isNestedTooDeep = areFoldersNestedTooDeep(
    folderStructure,
    currentFolder?.children?.map((subfolder) => subfolder.id),
    targetFolderDepth
  );

  const onDeleteFolder = async () => {
    setIsDeleting(true);
    try {
      if (shouldMoveStoriesOrFolders && targetFolderId !== currentFolderId) {
        const targetFolder = folderMap?.[targetFolderId];
        const targetFolderChildrenTitles = targetFolder?.children?.map((subfolder) => subfolder.displayName) || [];
        const subfolders = currentFolder?.children;
        if (subfolders?.length) {
          const subfoldersTitleIntersection = subfolders.filter((subfolder) =>
            targetFolderChildrenTitles.includes(subfolder.displayName)
          );
          if (subfoldersTitleIntersection.length) {
            await Promise.all(
              subfoldersTitleIntersection.map(async (subfolder) => {
                const title = generateUniqueFolderName(targetFolderChildrenTitles, subfolder.displayName);
                return renameStoryFolderMutation.mutateAsync({ id: subfolder.id, entityId, title });
              })
            );
          }
          await moveFoldersMutation.mutateAsync({
            ids: subfolders.map((subfolder) => subfolder.id),
            parentFolderId: targetFolderId
          });
        }
        await moveStoriesMutation.mutateAsync({ currentFolderId, targetFolderId });
        await deleteStoryFoldersMutation.mutateAsync({ folderIds: [currentFolderId], folderName: currentFolderName });
        props.onClose();
        setSelectedList({ id: targetFolderId, displayName: currentFolder?.displayName }, entitySubdomain);
      } else {
        await deleteStoryFoldersMutation.mutateAsync({
          folderIds: [currentFolderId, ...descendentFolderIds],
          folderName: currentFolderName
        });
        props.onClose();
        const currentFolderParentId = currentFolder?.parent;
        if (currentFolderParentId) {
          // set new folder selection to parent
          setSelectedList(
            { id: currentFolderParentId, displayName: folderMap?.[currentFolderParentId]?.displayName },
            entitySubdomain
          );
        } else {
          // reset to all stories folder in stories
          setSelectedList({ id: 'stories', displayName: messages.allStories }, entitySubdomain);
        }
      }
      toggleToast({
        title: messages.storyFolderDeleted,
        body: messages.storyFolderDeletedDescription({ title: currentFolderName }),
        severity: 'success'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderInfoBanner = (): ReactElement => {
    let banner;
    let storyText = currentFolderStoryCount === 1 ? 'Story' : 'Stories';
    const subfolderText = subfolderCount === 1 ? 'subfolder' : 'subfolders';

    if (shouldMoveStoriesOrFolders) {
      banner = (
        <Box sx={styles.deleteBanner}>
          <Alert severity="error" icon={false}>
            {messages.deleting}
            <Typography component="span" variant="body2" fontWeight="medium" data-test="folder-name">
              {`${currentFolderName}.`}
            </Typography>
          </Alert>
        </Box>
      );
    } else {
      if (subfolderCount > 0) {
        storyText = totalStoryCount === 1 ? 'Story' : 'Stories';
        banner = (
          <Box sx={styles.deleteBanner}>
            <Alert severity="error" icon={false}>
              {messages.deleting}
              <Typography component="span" variant="body2" fontWeight="medium" data-test="folder-name">
                {currentFolderName}
              </Typography>
              {messages.withStoriesAndSubfolders({ subfolderCount, subfolderText, totalStoryCount, storyText })}
            </Alert>
          </Box>
        );
      } else {
        banner = (
          <Box sx={styles.deleteBanner}>
            <Alert severity="error" icon={false}>
              {messages.deleting}
              <Typography component="span" variant="body2" fontWeight="medium" data-test="folder-name">
                {currentFolderName}
              </Typography>
              {messages.withStories({ currentFolderStoryCount, storyText })}
            </Alert>
          </Box>
        );
      }
    }
    return banner;
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
      data-test="dialog-delete-folder"
      aria-labelledby="delete-folder-dialog-title"
    >
      <DialogTitle id="delete-folder-dialog-title" sx={styles.dialogTitle}>
        {messages.deleteFolder}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body2" color="text.secondary">{messages.deleteFolderDescription}</Typography>

        {renderInfoBanner()}

        {(currentFolderStoryCount > 0 || subfolderCount > 0) && (
          <Box sx={styles.formItem}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={styles.formLabel}>
                {subfolderCount > 0 ? messages.deleteFolderStoriesAndSubfoldersPrompt : messages.deleteFolderStoriesPrompt}
              </FormLabel>
              <RadioGroup
                sx={styles.radioGroup}
                value={postFolderDeleteAction}
                onChange={(_e, value) => setPostFolderDeleteAction(value)}
                data-test="stories-post-delete-action-radio-group"
              >
                <FormControlLabel
                  value={MOVE_STORIES}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'radio-transfer-stories', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.moveStoriesToAnotherFolder}
                />
                <FormControlLabel
                  value={DELETE_STORIES}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'radio-delete-stories', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.deleteThem}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {shouldMoveStoriesOrFolders && (
          <Box sx={styles.formItem}>
            <Typography variant="body2" sx={styles.formLabel}>
              {subfolderCount > 0
                ? messages.whichFolderToTransferStoriesAndFolders
                : messages.whichFolderToTransferStories}
            </Typography>
            <FolderTreeSelect
              activeNodeId={targetFolderId}
              data={accordionOptions}
              dataTest="folder-accordion-select"
              disabled={!folderStructure}
              maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
              onNodeSelect={(node) => setTargetFolderId(node.id)}
              showFilter={true}
              filterPlaceholder={messages.filterFolders}
            />
            {isNestedTooDeep && (
              <FormHelperText error sx={styles.errorMsg}>{messages.nestedTooDeep}</FormHelperText>
            )}
          </Box>
        )}

        <Box sx={styles.formItem}>
          <TextField
            fullWidth
            required
            size="small"
            label={messages.deleteConfirmation}
            sx={styles.textField}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            inputProps={{ 'data-test': 'delete-confirmation-input', className: 'no-format' }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={props.onClose}
          data-test="cancel-delete-button"
        >
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          disabled={
            confirmationText !== CONFIRM_DELETE ||
            isNestedTooDeep ||
            (shouldMoveStoriesOrFolders && targetFolderId === currentFolderId) ||
            (shouldMoveStoriesOrFolders && !targetFolderId)
          }
          onClick={onDeleteFolder}
          data-test="confirm-delete-button"
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
        >
          {messages.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
